import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Check, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const INITIAL_Q = {
  company_name: "", industry: "", target_audience: "", brand_message: "",
  competitors: "", music_style: "", song_references: "", voice_preferences: "",
  lyrics_needed: true, deadline: "", budget: "", notes: "",
};

export default function OrderNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedPkg, setSelectedPkg] = useState(params.get("package") || "");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [q, setQ] = useState(INITIAL_Q);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/packages"), api.get("/addons")]).then(([p, a]) => {
      setPackages(p.data); setAddons(a.data);
    });
  }, []);

  const toggleAddon = (k) =>
    setSelectedAddons((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const pkg = packages.find((p) => p.key === selectedPkg);
  const addonsTotal = addons.filter((a) => selectedAddons.includes(a.key)).reduce((s, a) => s + a.price, 0);
  const total = (pkg?.price || 0) + addonsTotal;

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/orders", {
        package_key: selectedPkg, addons: selectedAddons, questionnaire: q,
      });
      nav(`/dashboard/orders/${data.id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-12 max-w-4xl">
        {/* Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {["Package", "Add-Ons", "Brief"].map((label, i) => {
            const stepNum = i + 1;
            let stepClass = "border-ink-700 text-white/40";
            if (step > stepNum) stepClass = "border-gold bg-gold text-black";
            else if (step === stepNum) stepClass = "border-gold text-gold";
            return (
              <div key={label} className="flex items-center gap-3" data-testid={`order-step-${stepNum}`}>
                <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center font-heading font-bold text-sm transition-colors ${stepClass}`}>
                  {step > stepNum ? <Check size={14} /> : stepNum}
                </div>
                <div className={`text-[11px] tracking-[0.22em] uppercase font-semibold ${step === stepNum ? "text-gold" : "text-white/50"}`}>{label}</div>
                {i < 2 && <div className="w-10 h-px bg-ink-700" />}
              </div>
            );
          })}
        </div>

        {/* Step 1 - Package */}
        {step === 1 && (
          <div data-testid="order-step-1-content">
            <h2 className="font-heading text-3xl mb-2">Select a variation package</h2>
            <p className="text-white/60 mb-8">Pay for variations after you hear them — every package includes the full creative process.</p>
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {packages.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setSelectedPkg(p.key)}
                  data-testid={`order-pkg-${p.key}`}
                  className={`text-left p-6 border-2 transition-all bg-ink-900 ${
                    selectedPkg === p.key ? "border-gold shadow-gold" : "border-ink-700 hover:border-gold/60"
                  }`}
                >
                  {p.recommended && (
                    <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold flex items-center gap-1 mb-2">
                      <Sparkles size={12} /> Recommended
                    </div>
                  )}
                  <div className="font-heading text-lg text-white">{p.name}</div>
                  <div className="font-heading text-3xl text-gold my-2">${p.price.toFixed(0)}</div>
                  {p.savings && <div className="text-gold text-xs">{p.savings}</div>}
                  <div className="text-white/60 text-sm mt-3">{p.description}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button disabled={!selectedPkg} onClick={() => setStep(2)} className="btn-gold" data-testid="order-next-1">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2 - Add-ons */}
        {step === 2 && (
          <div data-testid="order-step-2-content">
            <h2 className="font-heading text-3xl mb-2">Optional add-ons</h2>
            <p className="text-white/60 mb-8">Boost your song with extra versions, formats, and video productions. You can also add these later.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {addons.map((a) => {
                const active = selectedAddons.includes(a.key);
                return (
                  <button
                    key={a.key}
                    onClick={() => toggleAddon(a.key)}
                    data-testid={`order-addon-${a.key}`}
                    className={`text-left p-5 border-2 transition-all bg-ink-900 ${
                      active ? "border-gold shadow-gold" : "border-ink-700 hover:border-gold/60"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-heading text-base text-white">{a.name}</div>
                      <div className="text-gold font-semibold">${a.price.toFixed(0)}</div>
                    </div>
                    <div className="text-white/55 text-sm">{a.description}</div>
                  </button>
                );
              })}
            </div>
            <Total pkg={pkg} addonsTotal={addonsTotal} total={total} />
            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(1)} className="btn-outline" data-testid="order-back-2"><ArrowLeft size={16} /> Back</button>
              <button onClick={() => setStep(3)} className="btn-gold" data-testid="order-next-2">Continue <ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {/* Step 3 - Creative Brief */}
        {step === 3 && (
          <div data-testid="order-step-3-content">
            <h2 className="font-heading text-3xl mb-2">Creative brief</h2>
            <p className="text-white/60 mb-8">Tell us about your brand. The more we know, the better your songs.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Company Name *" value={q.company_name} onChange={(v) => setQ({ ...q, company_name: v })} testid="q-company-name" required />
              <Field label="Industry *" value={q.industry} onChange={(v) => setQ({ ...q, industry: v })} testid="q-industry" required />
              <Field label="Target Audience *" value={q.target_audience} onChange={(v) => setQ({ ...q, target_audience: v })} testid="q-audience" required />
              <Field label="Music Style *" value={q.music_style} onChange={(v) => setQ({ ...q, music_style: v })} placeholder="e.g. cinematic pop, indie folk, R&B" testid="q-style" required />
              <Field label="Competitors" value={q.competitors} onChange={(v) => setQ({ ...q, competitors: v })} testid="q-competitors" />
              <Field label="Voice Preferences" value={q.voice_preferences} onChange={(v) => setQ({ ...q, voice_preferences: v })} placeholder="male / female / multi / instrumental" testid="q-voice" />
              <Field label="Deadline" value={q.deadline} onChange={(v) => setQ({ ...q, deadline: v })} testid="q-deadline" />
              <Field label="Budget" value={q.budget} onChange={(v) => setQ({ ...q, budget: v })} testid="q-budget" />
              <TextArea label="Brand Message *" value={q.brand_message} onChange={(v) => setQ({ ...q, brand_message: v })} testid="q-message" required />
              <TextArea label="Song References" value={q.song_references} onChange={(v) => setQ({ ...q, song_references: v })} placeholder="links or artist/songs that inspire the vibe" testid="q-references" />
              <TextArea label="Additional Notes" value={q.notes} onChange={(v) => setQ({ ...q, notes: v })} testid="q-notes" full />
            </div>

            <div className="mt-6 flex items-center gap-3">
              <input type="checkbox" id="lyrics" checked={q.lyrics_needed} onChange={(e) => setQ({ ...q, lyrics_needed: e.target.checked })}
                     className="w-4 h-4 accent-gold" data-testid="q-lyrics-needed" />
              <label htmlFor="lyrics" className="text-white/80 text-sm">Include lyrics in the song</label>
            </div>

            <div className="mt-10">
              <Total pkg={pkg} addonsTotal={addonsTotal} total={total} />
            </div>
            {error && <div className="text-destructive mt-4 text-sm" data-testid="order-error">{error}</div>}
            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(2)} className="btn-outline" data-testid="order-back-3"><ArrowLeft size={16} /> Back</button>
              <button
                onClick={submit}
                disabled={submitting || !q.company_name || !q.industry || !q.target_audience || !q.music_style || !q.brand_message}
                className="btn-gold"
                data-testid="order-submit"
              >
                {submitting ? "Submitting…" : "Submit Order"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, required, placeholder = "" }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input required={required} className="input" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function TextArea({ label, value, onChange, testid, required, placeholder = "", full }) {
  return (
    <div className={full ? "sm:col-span-2" : "sm:col-span-2"}>
      <label className="label">{label}</label>
      <textarea required={required} rows={3} className="input resize-none" value={value} placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Total({ pkg, addonsTotal, total }) {
  return (
    <div className="bg-ink-900 border border-gold/30 p-5">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-white/70">Package: {pkg?.name}</span>
        <span className="text-white">${(pkg?.price || 0).toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-sm mb-3">
        <span className="text-white/70">Add-ons</span>
        <span className="text-white">${addonsTotal.toFixed(2)}</span>
      </div>
      <div className="border-t border-ink-700 pt-3 flex justify-between items-baseline">
        <span className="eyebrow">Estimated Total</span>
        <span className="font-heading text-2xl text-gold">${total.toFixed(2)}</span>
      </div>
      <p className="text-white/45 text-xs mt-3">
        You&apos;ll only be charged after you hear and approve the variations you want to keep.
      </p>
    </div>
  );
}
